import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Wand2, Save, Link2, X, CheckCircle2, DownloadCloud, Download } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import { listItems } from '@/api/items';
import { getShopifyUnmapped, getShopifySkuMap, bulkResolveShopifySkus, syncShopifyCatalog, type UnmappedRow, type SkuMapRow } from '@/api/shopify';

type Chosen = Record<string, { itemId: number; code: string; name: string }>;

const METHOD_TONE: Record<string, 'success' | 'info' | 'warning'> = { barcode: 'success', code: 'info', name: 'warning' };

export function ShopifyMappingPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [view, setView] = useState<'unmapped' | 'mapped' | 'all'>('unmapped');
  const [chosen, setChosen] = useState<Chosen>({});
  const [pickSku, setPickSku] = useState<string | null>(null);

  // Live search — debounce keystrokes into the applied query (unmapped view hits
  // the server; mapped/all views filter client-side). No Search button needed.
  useEffect(() => {
    const t = setTimeout(() => setApplied(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const query = useQuery({
    queryKey: ['shopify-unmapped', applied],
    queryFn: () => getShopifyUnmapped(applied || undefined),
    enabled: view === 'unmapped',
  });
  const data = query.data;
  const rows = data?.rows ?? [];

  // Mapped / All browse view (read-only list with a re-map action).
  const mapQuery = useQuery({
    queryKey: ['shopify-skumap', view],
    queryFn: () => getShopifySkuMap(view === 'mapped' ? 'mapped' : undefined),
    enabled: view !== 'unmapped',
  });
  const mapRows = useMemo(() => {
    const all = mapQuery.data?.data ?? [];
    if (!applied.trim()) return all;
    const q = applied.trim().toLowerCase();
    return all.filter((r) => [r.sku, r.shopifyBarcode, r.shopifyTitle, r.itemCode, r.itemName].some((v) => (v || '').toLowerCase().includes(q)));
  }, [mapQuery.data, applied]);

  const chosenCount = Object.keys(chosen).length;

  const syncMap = useMutation({
    mutationFn: syncShopifyCatalog,
    onSuccess: (s) => {
      toast.push(`Synced from Shopify — ${s.mapped}/${s.mappable} mapped (${s.matchedByBarcode} by barcode). Nothing was written to Shopify.`, 'success');
      qc.invalidateQueries({ queryKey: ['shopify-unmapped'] });
      qc.invalidateQueries({ queryKey: ['shopify-skumap'] });
    },
    onError: (e) => toast.push(apiError(e, 'Sync failed'), 'error'),
  });

  const save = useMutation({
    mutationFn: () => bulkResolveShopifySkus(Object.entries(chosen).map(([sku, v]) => ({ sku, itemId: v.itemId }))),
    onSuccess: (r) => {
      toast.push(`Mapped ${r.applied} SKU(s); ${r.linesBackfilled} order line(s) updated${r.errors.length ? `; ${r.errors.length} failed` : ''}.`, r.errors.length ? 'warning' : 'success');
      if (r.processedWithOtherItem?.length) toast.push(`Already processed with a different item — fix in the ERP order: ${r.processedWithOtherItem.join(', ')}`, 'warning');
      setChosen({});
      qc.invalidateQueries({ queryKey: ['shopify-unmapped'] });
      qc.invalidateQueries({ queryKey: ['shopify-skumap'] });
    },
    onError: (e) => toast.push(apiError(e, 'Bulk mapping failed'), 'error'),
  });

  function acceptAllSuggestions() {
    setChosen((prev) => {
      const next = { ...prev };
      for (const r of rows) {
        if (r.suggestedItemId != null && !next[r.sku]) {
          next[r.sku] = { itemId: r.suggestedItemId, code: r.suggestedCode, name: r.suggestedName };
        }
      }
      return next;
    });
  }
  function useSuggestion(r: UnmappedRow) {
    if (r.suggestedItemId == null) return;
    setChosen((p) => ({ ...p, [r.sku]: { itemId: r.suggestedItemId!, code: r.suggestedCode, name: r.suggestedName } }));
  }
  function clearChoice(sku: string) {
    setChosen((p) => { const n = { ...p }; delete n[sku]; return n; });
  }

  const suggestable = useMemo(() => rows.filter((r) => r.suggestedItemId != null && !chosen[r.sku]).length, [rows, chosen]);

  const chosenCell = (sku: string, mapped: boolean) => (chosen[sku] ? (
    <div className="flex items-center gap-2">
      <span className="flex items-center gap-1 text-success"><CheckCircle2 size={13} /> {chosen[sku].code}</span>
      <button type="button" onClick={() => clearChoice(sku)} className="text-[var(--text-muted)] hover:text-danger" title="Clear"><X size={13} /></button>
    </div>
  ) : (
    <Button variant="glass" size="sm" icon={<Link2 size={12} />} onClick={() => setPickSku(sku)}>{mapped ? 'Change' : 'Map'}</Button>
  ));

  // Export the SKU map filtered by the current View + applied Search (full set).
  function onExport() {
    const params = new URLSearchParams({ view });
    if (applied.trim()) params.set('search', applied.trim());
    downloadExcel(`/shopify/sku-map/export.xlsx?${params.toString()}`, `GRHAMOY_Shopify_SKU_Map_${view}.xlsx`, 120000)
      .catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }

  return (
    <div>
      <SectionHeader
        title="SKU Mapping"
      />

      <FilterBar>
        <FilterField label="Search" className="sm:w-[320px]">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="SKU, barcode or variant name" className="!pl-8" />
          </div>
        </FilterField>
        <FilterField label="View" className="sm:w-[230px]">
          <Select value={view} onChange={(e) => { setView(e.target.value as typeof view); setChosen({}); }}>
            <option value="unmapped">Unmapped (map these)</option>
            <option value="mapped">Mapped</option>
            <option value="all">All</option>
          </Select>
        </FilterField>
        <div className="col-span-3 flex flex-wrap items-center gap-2 sm:col-auto sm:ml-auto">
          <Button variant="primary" size="sm" icon={<DownloadCloud size={13} />} onClick={() => syncMap.mutate()} disabled={syncMap.isPending}
            title="Re-read the Shopify catalogue and map variants to ERP items by barcode. Nothing is written to Shopify.">
            {syncMap.isPending ? 'Syncing…' : 'Sync from Shopify'}
          </Button>
          {view === 'unmapped' && (
            <Button variant="glass" size="sm" icon={<Wand2 size={13} />} onClick={acceptAllSuggestions} disabled={!suggestable}
              title="Select the suggested ERP item for every row that has one">
              Accept suggestions{suggestable ? ` (${suggestable})` : ''}
            </Button>
          )}
          <Button variant="success" size="sm" icon={<Save size={13} />} onClick={() => save.mutate()} disabled={!chosenCount || save.isPending}>
            {save.isPending ? 'Saving…' : `Save ${chosenCount} mapping${chosenCount === 1 ? '' : 's'}`}
          </Button>
          {chosenCount > 0 && <Button variant="glass" size="sm" onClick={() => setChosen({})}>Clear</Button>}
          <Button variant="glass" size="sm" icon={<Download size={13} />} onClick={onExport} title="Export the current view + search to Excel">Export</Button>
        </div>
      </FilterBar>

      {view === 'unmapped' ? (
        <Table<UnmappedRow>
          paginated storageKey="shopify-unmapped" title="Unmapped Shopify Variants" count={data ? `Unmapped ${data.total} · showing ${data.count}` : 'Loading…'}
          rows={rows} rowKey={(r) => r.sku} loading={query.isFetching && !data}
          columns={[
            { key: 'sku', header: 'Shopify SKU', width: '260px', cell: (r) => (
              <div><strong>{r.sku}</strong>{r.shopifyBarcode ? <div className="text-[11px] text-[var(--text-muted)]">bc: {r.shopifyBarcode}</div> : null}</div>
            ) },
            { key: 'variant', header: 'Variant', width: '360px', cell: (r) => <span className="block max-w-[360px] truncate text-[var(--text-secondary)]" title={r.shopifyTitle ?? ''}>{r.shopifyTitle || '—'}</span> },
            { key: 'suggestion', header: 'Suggested ERP item', width: '230px', cell: (r) => (r.suggestedItemId != null ? (
              <div className="flex items-center gap-2">
                <Badge tone={METHOD_TONE[r.suggestMethod ?? 'name']}>{r.suggestMethod}</Badge>
                <span>{r.suggestedCode}</span>
                {!chosen[r.sku] && <Button variant="glass" size="sm" onClick={() => useSuggestion(r)}>Use</Button>}
              </div>
            ) : <span className="text-[var(--text-muted)]">— none —</span>) },
            { key: 'chosen', header: 'Map to', width: '170px', align: 'right', cell: (r) => chosenCell(r.sku, false) },
          ]}
        />
      ) : (
        <Table<SkuMapRow>
          paginated storageKey="shopify-skumap-manage" title={view === 'mapped' ? 'Mapped Variants' : 'All Variants'}
          count={mapQuery.data ? `${mapRows.length} shown · Mapped ${mapQuery.data.summary.mapped} · Unmapped ${mapQuery.data.summary.unmapped}` : 'Loading…'}
          rows={mapRows} rowKey={(r) => r.sku} loading={mapQuery.isFetching && !mapQuery.data}
          columns={[
            { key: 'sku', header: 'Shopify SKU', width: '240px', cell: (r) => <strong>{r.sku}</strong> },
            { key: 'barcode', header: 'Barcode', width: '120px', cell: (r) => <span className="font-mono text-xs text-[var(--text-muted)]">{r.shopifyBarcode || '—'}</span> },
            { key: 'variant', header: 'Variant', width: '340px', cell: (r) => <span className="block max-w-[340px] truncate text-[var(--text-secondary)]" title={r.shopifyTitle ?? ''}>{r.shopifyTitle || '—'}</span> },
            { key: 'erp', header: 'ERP Item', width: '240px', cell: (r) => {
              if (!r.mapped) return <Badge tone="danger">Unmapped</Badge>;
              const label = r.itemName || r.itemCode;
              return <span className="block max-w-[240px] truncate" title={label}>{label}</span>;
            } },
            { key: 'chosen', header: 'Map to', width: '170px', align: 'right', cell: (r) => chosenCell(r.sku, r.mapped) },
          ]}
        />
      )}

      <ItemPickerModal
        sku={pickSku}
        onClose={() => setPickSku(null)}
        onPick={(it) => { if (pickSku) setChosen((p) => ({ ...p, [pickSku]: it })); setPickSku(null); }}
      />
    </div>
  );
}

function ItemPickerModal({ sku, onClose, onPick }: { sku: string | null; onClose: () => void; onPick: (it: { itemId: number; code: string; name: string }) => void }) {
  const [q, setQ] = useState('');
  const search = useQuery({
    queryKey: ['item-search-bulk', q],
    queryFn: () => listItems({ search: q, status: 'Active', pageSize: 20 }),
    enabled: sku != null && q.trim().length >= 2,
  });
  const items = search.data?.data ?? [];
  return (
    <Modal open={sku != null} onClose={onClose} size="md" title={<span className="flex items-center gap-2"><Link2 size={16} /> Map “{sku}” to an ERP item</span>}>
      <div className="space-y-3">
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item by code, name or barcode (min 2 chars)" />
        <div className="max-h-[320px] overflow-y-auto rounded-card border border-[var(--border-color)]">
          {q.trim().length < 2 ? (
            <div className="py-8 text-center text-xs text-[var(--text-muted)]">Type at least 2 characters to search.</div>
          ) : search.isFetching ? (
            <div className="py-8 text-center text-xs text-[var(--text-muted)]">Searching…</div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-xs text-[var(--text-muted)]">No items found.</div>
          ) : (
            items.map((it) => (
              <button key={it.id} type="button" onClick={() => onPick({ itemId: it.id, code: it.code, name: it.name })}
                className="flex w-full items-center justify-between gap-3 border-b border-[var(--border-color)] px-3 py-2 text-left last:border-b-0 hover:bg-[var(--bg-secondary)]">
                <div>
                  <div className="text-sm font-semibold">{it.code}</div>
                  <div className="text-xs text-[var(--text-muted)]">{it.name}</div>
                </div>
                {it.barcode && <div className="text-right text-xs text-[var(--text-muted)]">{it.barcode}</div>}
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
